import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Save, Trash2, Search, BookOpen, Lock, Users, Globe, RefreshCw, Loader2 } from "lucide-react";

const CATEGORY_LABELS = {
  personality: "AI Personality",
  company: "Company Information",
  brands: "Brands",
  pricing: "Product Pricing",
  dtf: "DTF Settings",
  puff: "Puff Settings",
  design_prefs: "Design Preferences",
  colors: "Brand Colors",
  schools_teams: "Schools & Teams",
  faqs: "FAQs",
  policies: "Policies",
  marketing: "Marketing Rules",
  production: "Production Rules",
  other: "Other Business Knowledge",
};

const VISIBILITY_META = {
  admin:    { label: "Owner Only", icon: Lock,   color: "text-red-700 bg-red-50 border-red-200" },
  customer: { label: "Customer Only", icon: Users, color: "text-blue-700 bg-blue-50 border-blue-200" },
  both:     { label: "Everyone", icon: Globe, color: "text-green-700 bg-green-50 border-green-200" },
};

function VisibilityBadge({ v }) {
  const meta = VISIBILITY_META[v] || VISIBILITY_META.admin;
  const Icon = meta.icon;
  return <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 flex items-center gap-1 ${meta.color}`}><Icon size={9}/> {meta.label}</span>;
}

function KnowledgeEntry({ item, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...item, keywords: (item.keywords || []).join(", ") });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...draft, keywords: draft.keywords.split(",").map(s => s.trim()).filter(Boolean) };
      await onSave(payload);
      setEditing(false);
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    setBusy(true);
    try { await onDelete(item.knowledge_id); } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div className="border border-neutral-200 bg-white p-4" data-testid={`kb-item-${item.knowledge_id}`}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-medium text-[color:var(--hb-navy)]">{item.title}</h3>
              <VisibilityBadge v={item.visibility}/>
            </div>
            <p className="text-xs text-neutral-600 mt-1.5 leading-relaxed">{item.content}</p>
            {item.keywords?.length > 0 && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {item.keywords.map((k, i) => <span key={i} className="text-[10px] bg-neutral-100 px-1.5 py-0.5 text-neutral-600">{k}</span>)}
              </div>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setEditing(true)} className="text-[10px] uppercase tracking-widest border border-neutral-300 px-2 py-1 hover:bg-neutral-50" data-testid={`kb-edit-${item.knowledge_id}`}>Edit</button>
            <button onClick={remove} disabled={busy} className="text-[10px] uppercase tracking-widest border border-red-300 text-red-700 px-2 py-1 hover:bg-red-50" data-testid={`kb-delete-${item.knowledge_id}`}><Trash2 size={10}/></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-[color:var(--hb-royal)] bg-white p-4" data-testid={`kb-edit-form-${item.knowledge_id}`}>
      <input value={draft.title} onChange={e => setDraft({...draft, title: e.target.value})} placeholder="Title" className="w-full text-sm font-medium border border-neutral-300 px-2 py-1.5 mb-2" data-testid="kb-title-input"/>
      <textarea value={draft.content} onChange={e => setDraft({...draft, content: e.target.value})} rows={4} placeholder="Content" className="w-full text-xs border border-neutral-300 px-2 py-1.5 mb-2 resize-none" data-testid="kb-content-input"/>
      <input value={draft.keywords} onChange={e => setDraft({...draft, keywords: e.target.value})} placeholder="Keywords (comma separated)" className="w-full text-xs border border-neutral-300 px-2 py-1.5 mb-2" data-testid="kb-keywords-input"/>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <select value={draft.visibility} onChange={e => setDraft({...draft, visibility: e.target.value})} className="text-xs border border-neutral-300 px-2 py-1.5" data-testid="kb-visibility-select">
          <option value="admin">Owner Only</option>
          <option value="customer">Customer Only</option>
          <option value="both">Everyone</option>
        </select>
        <div className="flex gap-2">
          <button onClick={() => setEditing(false)} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1.5">Cancel</button>
          <button onClick={save} disabled={busy} className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-3 py-1.5 flex items-center gap-1" data-testid="kb-save-btn">
            {busy ? <Loader2 size={10} className="animate-spin"/> : <Save size={10}/>} Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BusinessKnowledge() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(Object.keys(CATEGORY_LABELS));
  const [activeCat, setActiveCat] = useState("personality");
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState({ category: "personality", title: "", content: "", keywords: "", visibility: "admin" });
  const [usage, setUsage] = useState(null);

  useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const [k, u] = await Promise.all([
        api.get("/admin/knowledge"),
        api.get("/admin/ai/usage?days=30").catch(() => ({data:null})),
      ]);
      setItems(k.data.items || []);
      setUsage(u.data);
    } catch { toast.error("Failed to load knowledge base"); }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const saveEntry = async (payload) => {
    try {
      await api.put(`/admin/knowledge/${payload.knowledge_id}`, {
        category: payload.category, title: payload.title, content: payload.content, keywords: payload.keywords, visibility: payload.visibility,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); throw e; }
  };
  const deleteEntry = async (id) => {
    try { await api.delete(`/admin/knowledge/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const createEntry = async () => {
    if (!newDraft.title || !newDraft.content) { toast.error("Title & content required"); return; }
    try {
      await api.post("/admin/knowledge", {
        ...newDraft,
        keywords: newDraft.keywords.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Added");
      setAdding(false);
      setNewDraft({ category: activeCat, title: "", content: "", keywords: "", visibility: "admin" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const filtered = items.filter(i => {
    if (i.category !== activeCat) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (i.title || "").toLowerCase().includes(q) || (i.content || "").toLowerCase().includes(q) || (i.keywords || []).some(k => (k || "").toLowerCase().includes(q));
  });

  const categoryCounts = categories.reduce((acc, c) => ({...acc, [c]: items.filter(i => i.category === c).length}), {});

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="knowledge-page">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen size={22} className="text-[color:var(--hb-royal)]"/>
        <h1 className="font-display text-4xl text-[color:var(--hb-navy)]">AI & Business Knowledge</h1>
      </div>
      <p className="text-sm text-neutral-600 mb-6">Everything the AI knows about your business. Only owner-visible entries are shared with the customer chat. Update rules, prices, and policies here — no code changes required.</p>

      {/* Cost meter */}
      {usage && (
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="cost-meter">
          <div className="p-3 border border-neutral-200 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Last 30d cost</div>
            <div className="text-2xl font-medium text-[color:var(--hb-navy)]">${usage.total_cost_usd.toFixed(4)}</div>
          </div>
          <div className="p-3 border border-neutral-200 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">AI requests</div>
            <div className="text-2xl font-medium text-[color:var(--hb-navy)]">{usage.total_calls}</div>
          </div>
          <div className="p-3 border border-neutral-200 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Top model</div>
            <div className="text-sm font-medium text-[color:var(--hb-navy)] mt-2">{usage.by_task?.[0]?.model || "—"}</div>
          </div>
        </div>
      )}

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-neutral-200 pb-3">
        {categories.map(c => (
          <button
            key={c}
            onClick={() => { setActiveCat(c); setNewDraft(d => ({...d, category: c})); setAdding(false); }}
            className={`text-xs uppercase tracking-widest px-3 py-1.5 border transition ${activeCat === c ? "bg-[color:var(--hb-navy)] text-white border-[color:var(--hb-navy)]" : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"}`}
            data-testid={`kb-tab-${c}`}
          >
            {CATEGORY_LABELS[c]} {categoryCounts[c] > 0 && <span className="ml-1 opacity-70">({categoryCounts[c]})</span>}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search entries…" className="w-full pl-9 pr-3 py-2 border border-neutral-300 text-sm" data-testid="kb-search"/>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-2 flex items-center gap-1" data-testid="kb-refresh"><RefreshCw size={12}/> Refresh</button>
        <button onClick={() => { setAdding(true); setNewDraft(d => ({...d, category: activeCat, title: "", content: "", keywords: ""})); }} className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-3 py-2 flex items-center gap-1" data-testid="kb-add-btn">
          <Plus size={12}/> Add
        </button>
      </div>

      {adding && (
        <div className="border-2 border-[color:var(--hb-royal)] bg-white p-4 mb-4" data-testid="kb-new-form">
          <div className="text-xs uppercase tracking-widest text-[color:var(--hb-royal)] mb-2">New entry in {CATEGORY_LABELS[activeCat]}</div>
          <input value={newDraft.title} onChange={e => setNewDraft({...newDraft, title: e.target.value})} placeholder="Title (e.g., 'School team pricing')" className="w-full text-sm font-medium border border-neutral-300 px-2 py-1.5 mb-2" data-testid="kb-new-title"/>
          <textarea value={newDraft.content} onChange={e => setNewDraft({...newDraft, content: e.target.value})} rows={4} placeholder="Content — the actual rule / info the AI should use" className="w-full text-xs border border-neutral-300 px-2 py-1.5 mb-2 resize-none" data-testid="kb-new-content"/>
          <input value={newDraft.keywords} onChange={e => setNewDraft({...newDraft, keywords: e.target.value})} placeholder="Keywords (comma separated) — helps the AI retrieve this at the right moment" className="w-full text-xs border border-neutral-300 px-2 py-1.5 mb-2" data-testid="kb-new-keywords"/>
          <div className="flex items-center gap-2 justify-between">
            <select value={newDraft.visibility} onChange={e => setNewDraft({...newDraft, visibility: e.target.value})} className="text-xs border border-neutral-300 px-2 py-1.5" data-testid="kb-new-visibility">
              <option value="admin">Owner Only</option>
              <option value="customer">Customer Only</option>
              <option value="both">Everyone</option>
            </select>
            <div className="flex gap-2">
              <button onClick={() => setAdding(false)} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1.5">Cancel</button>
              <button onClick={createEntry} className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-3 py-1.5 flex items-center gap-1" data-testid="kb-new-save"><Save size={10}/> Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-neutral-300 text-sm text-neutral-500" data-testid="kb-empty">
            No entries in <strong>{CATEGORY_LABELS[activeCat]}</strong> yet. Click "Add" to create one.
          </div>
        ) : filtered.map(item => (
          <KnowledgeEntry key={item.knowledge_id} item={item} onSave={saveEntry} onDelete={deleteEntry}/>
        ))}
      </div>

      <div className="mt-8 flex gap-4 text-xs">
        <Link to="/admin" className="text-[color:var(--hb-royal)] uppercase tracking-widest" data-testid="back-to-admin">← Admin home</Link>
        <Link to="/admin/chat" className="text-[color:var(--hb-royal)] uppercase tracking-widest" data-testid="link-chat">Open Assistant →</Link>
      </div>
    </div>
  );
}
